// ReportChartStackedMonth.jsx

import React, { useMemo, useState } from "react";

import PropTypes from "prop-types";
import { Box, Stack, Typography, Container } from "@mui/material";
import EChartsReact from "echarts-for-react";

import HoverPaper from "./shared/HoverPaper";
import Colours from "../helpers/colours";
import ToggleButtonTitled from "./ToggleButtonTitled";

/** -------------------------------------------------------------------------
 *  ReportChartStackedMonth
 *  ------------------------------------------------------------------------
 *  Stacked (or line) chart that shows the monthly commission breakdown for
 *  up to the last five years.  Each year is a separate series that can be
 *  toggled on/off via the legend buttons directly under the chart.
 *
 *  Props
 *  -----
 *  @param {Object} data   -> output from the existing `process()` helper that
 *                            uses keys_groups = [GROUP_DATE_MONTH, GROUP_DATE_YEAR].
 *                            Expect the shape:
 *                            {
 *                              "1/2024": { result: { commissionNotCancelled: 1234 } },
 *                              ...
 *                            }
 *  @param {String} metric -> which metric in `result` to visualise. Defaults
 *                            to "commissionNotCancelled".
 * ---------------------------------------------------------------------- */

const monthLabels = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const defaultColours = ["#000000", "#353535", "#696969", "#9e9e9e", "#d1d1d1"];

const ReportChartStackedMonth = ({
    data = {},
    metric = "commissionNotCancelled",
    title,
    datatype,
    fromDay,
    toDay,
    useRangeMonths = false,
    useAllYears = false,
}) => {
    const selectedMonths = useMemo(() => {
        if (!useRangeMonths) {
            return new Set(Array.from({ length: 12 }, (_, i) => i));
        }

        if (!fromDay || !toDay) {
            return new Set(Array.from({ length: 12 }, (_, i) => i));
        }

        const start = new Date(fromDay);
        const end = new Date(toDay);

        start.setUTCHours(0, 0, 0, 0);
        start.setUTCDate(1);

        const monthsSet = new Set();
        const cursor = new Date(start);
        let count = 0;

        while (cursor <= end) {
            monthsSet.add(cursor.getUTCMonth()); // 0-based month
            cursor.setUTCMonth(cursor.getUTCMonth() + 1);
            count++;
        }

        if (count > 12 || monthsSet.size === 12) {
            return new Set(Array.from({ length: 12 }, (_, i) => i));
        }

        return monthsSet;
    }, [fromDay, toDay, useRangeMonths]);

    /* 1️⃣  Build raw matrix [month][year] = value, but keep only the
     *     months contained in `selectedMonths`.                */
    const { matrix, years, labels } = useMemo(() => {
        const auxMatrix = Array.from({ length: 12 }, () => ({}));
        const auxYears = new Set();

        Object.entries(data).forEach(([key, value]) => {
            const [monthStr, yearStr] = key.split("/");
            const monthIdx = parseInt(monthStr, 10) - 1; // 0-based
            if (useRangeMonths && !selectedMonths.has(monthIdx)) return;

            const year = parseInt(yearStr, 10);
            const val = Math.round((value.result?.[metric] ?? 0) * 100) / 100;

            auxMatrix[monthIdx][year] = (auxMatrix[monthIdx][year] || 0) + val;
            auxYears.add(year);
        });

        const orderedYears = [...auxYears].sort((a, b) => b - a);

        // Remove months that are both (a) outside the range OR (b) have 0 data
        const filteredRows = [];
        const filteredLabels = [];
        auxMatrix.forEach((row, idx) => {
            if (useRangeMonths && !selectedMonths.has(idx)) return;
            const hasData = orderedYears.some((y) => row[y]); // (b)
            if (!hasData) return;
            filteredRows.push(row);
            filteredLabels.push(monthLabels[idx]);
        });

        return {
            matrix: filteredRows,
            years: orderedYears,
            labels: filteredLabels,
        };
    }, [data, metric, datatype, selectedMonths]);

    /** ------------------------------------------------------------------
     * 2️⃣  UI state – which years are visible & bar/line toggle
     * ----------------------------------------------------------------- */
    const [displayType, setDisplayType] = useState("bar");

    /** --------------------------------------------------------------
     * 3️⃣  Build ECharts option
     * ------------------------------------------------------------- */
    const option = useMemo(() => {
        const series = years.map((year, idx) => ({
            name: year.toString(),
            type: displayType,
            stack: "total",
            data: matrix.map((row) => row[year] || 0),
            emphasis: { focus: "series" },
            itemStyle: {
                color: defaultColours[idx % defaultColours.length],
            },
            label: {
                show: true,
                position: "inside",
                formatter: (params) => {
                    // Abbreviate large numbers, e.g. 120000 -> 120k
                    const v = params.value;
                    return v >= 1000 ? `${Math.round(v / 1000)}k` : v;
                },
            },
        }));

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow" },
                formatter: (params) => {
                    const total = params.reduce((acc, p) => acc + p.value, 0);
                    const month = params[0]?.axisValueLabel || "";
                    let html = `<strong>${month}:  ${total.toLocaleString()}</strong><br/>`;
                    params.forEach((p) => {
                        html += `${p.marker} ${p.seriesName}: <strong>${
                            p.value > 0 ? p.value.toLocaleString() : 0
                        }</strong><br/>`;
                    });
                    return html;
                },
            },
            legend: { show: true, bottom: 0 },
            grid: { left: 50, right: 20, bottom: 50, top: 20 },
            xAxis: {
                type: "category",
                data: labels,
                axisLabel: {
                    interval: 0,
                    formatter: (value) => value.substring(0, 3), // Jan, Feb…
                },
            },
            yAxis: {
                type: "value",
                axisLabel: {
                    formatter: (v) => (v >= 1000 ? `${v / 1000}k` : v),
                },
            },
            series,
        };
    }, [matrix, years, displayType, datatype]);

    if (!years.length) {
        return <Typography>No data for the selected filters.</Typography>;
    }

    /** --------------------------------------------------------------
     * 4️⃣  Render
     * ------------------------------------------------------------- */
    return (
        <Box>
            {title && (
                <Typography fontWeight={"bold"} sx={{ pt: 2 }}>
                    {title}
                </Typography>
            )}
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                <Stack spacing={1}>
                    <ToggleButtonTitled
                        TITLE="Display with:"
                        values={{ bar: "Bars", line: "Line" }}
                        valueSet={displayType}
                        onChange={(e, v) => v && setDisplayType(v)}
                    />

                    <Box sx={{ width: "100%", height: 420 }}>
                        <EChartsReact
                            notMerge={true}
                            key={`${datatype}-${years.join("-")}-${labels.join(
                                "-"
                            )}`} // ← ensures refresh on range change
                            option={option}
                            style={{ width: "100%", height: "100%" }}
                        />
                    </Box>

                    {/* Year toggle */}
                    {/* <ToggleButtonGroup
                        value={visibleYears}
                        onChange={handleToggleYear}
                        size="small"
                        sx={{ flexWrap: "wrap", justifyContent: "center" }}
                    >
                        {years.map((y) => (
                            <ToggleButton
                                key={y}
                                value={y}
                                sx={{ textTransform: "none" }}
                            >
                                {y}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup> */}
                </Stack>
            </Container>
        </Box>
    );
};

ReportChartStackedMonth.propTypes = {
    data: PropTypes.object.isRequired,
    metric: PropTypes.string,
    datatype: PropTypes.string,
    fromDay: PropTypes.string,
    toDay: PropTypes.string,
};

export default ReportChartStackedMonth;
